import React, { useState } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Message } from "primereact/message";
import { syncChargePoints } from "../../../../services/clearing/fleet-dashboard";
import type { ProjectionSeedResponse } from "../../../../types/chargingStationDashboard";
import { isFleetFailure } from "../../../../types/chargingStationDashboard";
import { formatNumber } from "../../../../utils/fleetDerivations";

interface SyncChargePointsProps {
  /** Opens the recompute, which applies the AC/DC split to past months. */
  onRecompute: () => void;
}

/**
 * BOF-76 — reads every charge point's power type and status from station-ops into clearing.
 *
 * Until this has run once, clearing records no nightly charge-point count and no AC/DC split: the only
 * charge points it knows are the ones that happened to send an event, and counting those would report a
 * fraction of the fleet as the fleet. It runs with the signed-in admin's own authority (clearing forwards
 * the token to station-ops), and is safe to repeat: a charge point an event has updated since is left as
 * the event set it.
 */
const SyncChargePoints: React.FC<SyncChargePointsProps> = ({ onRecompute }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ProjectionSeedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    const outcome = await syncChargePoints();
    setRunning(false);
    if (isFleetFailure(outcome)) {
      setError(outcome.message);
      return;
    }
    setResult(outcome.data);
  };

  const ask = () =>
    confirmDialog({
      header: "Sync charge points from station-ops?",
      message:
        "Reads every charge point's power type and status into clearing. Nightly charge-point counts and the AC/DC split start from the next run; recompute afterwards to apply the split to past months.",
      icon: "pi pi-sync",
      acceptLabel: "Sync",
      rejectLabel: "Cancel",
      accept: run,
    });

  return (
    <>
      <ConfirmDialog />
      <Button label="Sync charge points" icon="pi pi-sync" outlined loading={running} onClick={ask} />
      {(result || error) && (
        <div className="w-full">
          {error && <Message severity="error" className="w-full justify-content-start" text={error} />}
          {result && (
            <Message
              severity="success"
              className="w-full justify-content-start"
              content={
                <div className="flex flex-wrap align-items-center gap-2">
                  <span>
                    Synced {formatNumber(result.seeded)} charge points ({formatNumber(result.withPowerType)} with a power
                    type
                    {result.unclassified > 0 && `, ${formatNumber(result.unclassified)} without one`}
                    {result.skippedNewer > 0 && `; ${formatNumber(result.skippedNewer)} already newer from events`}).
                    Counts start with tonight's run. Recompute to apply the AC/DC split to past months.
                  </span>
                  <Button label="Open recompute" icon="pi pi-database" size="small" text onClick={onRecompute} />
                </div>
              }
            />
          )}
        </div>
      )}
    </>
  );
};

export default SyncChargePoints;
